import NotificationDotIconSVG from "assets/svg/notification-dot-icon";
import Button from "components/button/button";
import moment from "moment";
import React from "react";

import ProfileDefault from "assets/images/profile_default.png";
import { useTranslation } from "react-i18next";
import { LiaTimesSolid } from "react-icons/lia";
import { currentLang } from "utils/utils";

// show_adult arrives as 0/1 (sometimes "0"/"1" or boolean)
const isAdultFlag = (value) => value === true || Number(value) === 1;

/**
 * Sender name. Normal: plain/clickable. Blocked: not clickable, shows a
 * tooltip on hover or keyboard focus explaining why.
 */
const SenderName = ({ children, blocked = false, tooltip, onClick }) => {
  if (!blocked) {
    return (
      <span
        className={`font-semibold ${onClick ? "hover:text-primary" : ""}`}
        onClick={onClick}
      >
        {children}
      </span>
    );
  }

  return (
    <span className="relative group inline-block" tabIndex={0}>
      <span className="font-semibold cursor-not-allowed" aria-disabled="true">
        {children}
      </span>
      <span
        role="tooltip"
        className="hidden group-hover:block group-focus:block absolute left-0 top-full mt-1 z-50 w-64 p-2 rounded-md bg-lightBlack text-white text-xs font-normal leading-4 shadow-lg"
      >
        {tooltip}
      </span>
    </span>
  );
};

// notes
//type = follow or invitation
export default function NotificationContentItem({
  id,
  user_access_notification = {},
  is_read,
  created_at,
  type,
  acceptInvite, //for testing only please remove
  markReadNotification = () => {},
  onFollowerNameClick = () => {},
  reverse = false,
  removeFromList = () => {},
  profile, // the logged-in owner's profile (needs show_adult)
}) {
  const from = user_access_notification?.from || {};
  const user_access = user_access_notification?.user_access || {};
  const { t } = useTranslation();
  const lang = currentLang() === "ch" ? "zh-cn" : currentLang() || "en";

  // Adult-content rule: sender's profile is adult AND owner has not opted in.
  // Fails closed: if the owner's flag is missing, adult senders stay hidden.
  const senderIsAdult = isAdultFlag(from?.profile?.has_adult_content);
  const ownerAllowsAdult = isAdultFlag(profile?.show_adult);
  const isBlocked = senderIsAdult && !ownerAllowsAdult;

  const blockedTooltip = t(
    "adultContentBlockedTooltip",
    "This profile contains adult content. You have set your profile to not show adult content."
  );

  const fullName = `${from?.first_name || ""} ${from?.last_name || ""}`.trim();
  const mentionName = from?.profile?.profile_name || fullName;

  const avatar = isBlocked
    ? ProfileDefault
    : from?.profile?.profile_image_webp_url || from?.profile?.profile_image_url || ProfileDefault;

  const nameProps = { blocked: isBlocked, tooltip: blockedTooltip };

  return (
    <li
      className={`w-full flex ${reverse ? "flex-row-reverse gap-4" : "justify-between"} ${
        markReadNotification ? " cursor-pointer" : ""
      }`}
      onClick={(e) => markReadNotification(e, id)}
    >
      <div className={`flex ${reverse ? "flex-1" : "w-16/20 "}`}>
        {/* new */}
        {!reverse && <div className="w-3.5 pl-2">{!is_read && <NotificationDotIconSVG />}</div>}
        {/*  */}
        <div className="w-full pl-2">
          {/* notification description */}
          <div className="inline-flex items-center">
            <>
              {type === "profile_invite" && (
                <p className="text-base leading-6 text-lightBlack">
                  <SenderName {...nameProps}>{fullName}</SenderName>{" "}
                  {t("hasInvitedYouToBe")} {user_access?.permission?.name} {t("onTheProfile")}
                  <span className="font-semibold text-primary">
                    {" " + user_access?.profile?.profile_name}
                  </span>
                </p>
              )}
              {type === "profile_invite_response" && (
                <p className="text-base leading-6 text-lightBlack">
                  <SenderName {...nameProps}>{fullName}</SenderName>{" "}
                  {t("has")} {user_access?.status + " "}
                  {t("yourInvitationTobe")}{" "}
                  {user_access?.permission?.name === "Admin"
                    ? `${t("an")} ${user_access?.permission?.name}`
                    : `${t("a")} ${user_access?.permission?.name}`}{" "}
                  {t("ofYourProfile")}
                  <span className="font-semibold text-primary">
                    {" " + user_access?.profile?.profile_name}
                  </span>
                  .
                </p>
              )}
              {type === "following" && (
                <p className="text-base leading-6 text-lightBlack">
                  <SenderName
                    {...nameProps}
                    onClick={isBlocked ? undefined : onFollowerNameClick}
                  >
                    {fullName}
                  </SenderName>{" "}
                  {t("startedFollowingYou")}
                </p>
              )}

              {type === "new_milestone" && (
                <p className="text-base leading-6 text-lightBlack">
                  <SenderName {...nameProps}>{fullName}</SenderName>{" "}
                  {t("postedAMilestone")}
                </p>
              )}

              {type === "mention_milestone" && (
                <p className="text-base leading-6 text-lightBlack">
                  <SenderName {...nameProps}>{mentionName}</SenderName>{" "}
                  {t("taggedYouInAMilestone", "tagged you in a milestone")}
                </p>
              )}

              {/* button */}
              {type === "profile_invite" && user_access?.status === "pending" && (
                <div className="w-full mt-2">
                  <div className="w-full flex space-x-2">
                    <Button
                      buttonWidth="100%"
                      buttonName="Decline"
                      buttonTextClass="text-sm text-lightBlack font-semibold leading-4.4"
                      buttonRadius="6px"
                      buttonClass="relative py-3 bg-thBg"
                      onClick={acceptInvite}
                    />
                    <Button
                      buttonWidth="100%"
                      buttonName="Accept"
                      buttonTextClass="text-sm text-white font-semibold leading-4.4"
                      buttonRadius="6px"
                      buttonClass="relative py-3 bg-primary"
                      onClick={acceptInvite}
                    />
                  </div>
                </div>
              )}
            </>

            {reverse && <div className="w-3.5 pl-2">{!is_read && <NotificationDotIconSVG />}</div>}
          </div>

          {/* data */}
          <div className="w-full mt-2">
            <p className="text-xs font-semibold leading-3.5 text-notificationText">
              {moment(created_at).locale(lang).fromNow()}
            </p>
          </div>
        </div>
        {reverse /**Don't show in notification page only in header */ && (
          <div className="h-full flex items-start mr-4">
            <LiaTimesSolid
              className="text-sm cursor-pointer"
              onClick={(e) => removeFromList(e, id)}
            />
          </div>
        )}
      </div>
      <div className="w-10.5 h-10.5 rounded-md flex gap-1">
        <img src={avatar} alt="milestone-profile" className="rounded-md w-10.5 h-10.5" />
        {!reverse /**Don't show in notification page only in header */ && (
          <div className="h-full flex items-center">
            <LiaTimesSolid
              className="text-sm cursor-pointer"
              onClick={(e) => removeFromList(e, id)}
            />
          </div>
        )}
      </div>
    </li>
  );
}
